/* Public looks of other players (name, photo or icon, color, XP), fetched in batches and cached. */
import { useEffect, useState } from 'react'
import { call, backendReady } from './supa'
import type { Look } from '../ui/Avatar'

export interface Person extends Look {
  id: string
  username: string
  displayName: string
  equipped?: Record<string, string | null>
  premium?: boolean
}

const cache = new Map<string, Person>()
const waiting = new Map<string, ((p: Person | null) => void)[]>()
let queue = new Set<string>()
let timer: ReturnType<typeof setTimeout> | null = null

/* eslint-disable @typescript-eslint/no-explicit-any */
function toPerson(id: string, j: any): Person {
  return { id, username: j.username, displayName: j.display_name, name: j.display_name || j.username, avatar: j.avatar_url, icon: j.icon, color: j.color, xp: Number(j.xp) || 0, equipped: j.equipped || {}, premium: !!j.premium }
}

async function flush() {
  timer = null
  const ids = [...queue]
  queue = new Set()
  if (!ids.length) return
  let got: Record<string, any> = {}
  try {
    if (backendReady) got = await call<Record<string, any>>('looks', { ids })
  } catch { /* leave them unknown */ }
  for (const id of ids) {
    const p = got[id] ? toPerson(id, got[id]) : null
    if (p) cache.set(id, p)
    const ws = waiting.get(id) || []
    waiting.delete(id)
    ws.forEach((f) => f(p))
  }
}

export function person(id: string): Promise<Person | null> {
  const hit = cache.get(id)
  if (hit) return Promise.resolve(hit)
  return new Promise((res) => {
    const list = waiting.get(id) || []
    list.push(res)
    waiting.set(id, list)
    queue.add(id)
    if (!timer) timer = setTimeout(flush, 30)
  })
}
export const cachedPerson = (id: string) => cache.get(id) || null
export function rememberPerson(p: Person) {
  cache.set(p.id, p)
}

/* Look up many at once and re-render when they arrive. */
export function usePeople(ids: (string | null | undefined)[]): Record<string, Person> {
  const key = ids.filter(Boolean).join(',')
  const [map, setMap] = useState<Record<string, Person>>(() => {
    const m: Record<string, Person> = {}
    for (const id of ids) if (id && cache.has(id)) m[id] = cache.get(id)!
    return m
  })
  useEffect(() => {
    let alive = true
    const want = ids.filter((x): x is string => !!x)
    Promise.all(want.map(person)).then((list) => {
      if (!alive) return
      const m: Record<string, Person> = {}
      list.forEach((p) => { if (p) m[p.id] = p })
      setMap(m)
    })
    return () => { alive = false }
  }, [key])
  return map
}
